import React from 'react'
import { useTar } from '@/modules'
import { COVER_IMAGE_SIZES, toCoverVariantUrl, type CoverImageSize } from '@/utils/challengeCover.utils'
import { useCoverObjectPositionY } from '@/hooks/useCoverObjectPositionY'

interface CoverImagePreviewProps {
  // The ghost base path stored in coverImageUrl, e.g. "covers/my-task.webp".
  base?: string
  // Vertical focal point (0-100) applied as object-position Y, so the preview
  // matches how the cover is clipped on the live surfaces.
  focusY?: number
}

// One derived variant: live <img> probe + load/error badge so the admin can
// see at a glance whether the manually-uploaded file actually resolves.
const VariantPreview = ({
  url,
  caption,
  boxClassName,
  focusY,
}: {
  url: string
  caption: string
  boxClassName: string
  focusY?: number
}) => {
  const { t } = useTar()
  // 'loading' | 'ok' | 'error' — keyed on url by the parent so it resets on edit.
  const [status, setStatus] = React.useState<'loading' | 'ok' | 'error'>('loading')
  const { containerRef, imgRef, onImgLoad, objectPositionY } = useCoverObjectPositionY(focusY)

  return (
    <div className="space-y-1.5">
      <div ref={containerRef} className={`relative overflow-hidden rounded-md border border-border bg-muted/30 ${boxClassName}`}>
        <img
          ref={imgRef}
          src={url}
          alt=""
          className="h-full w-full object-cover"
          style={{ objectPosition: `50% ${objectPositionY}%` }}
          onLoad={() => { setStatus('ok'); onImgLoad() }}
          onError={() => setStatus('error')}
        />
        {status === 'error' && (
          <div className="absolute inset-0 grid place-items-center bg-error/10 px-2 text-center text-[11px] font-medium text-error">
            {t('tasks.coverImage.notFound')}
          </div>
        )}
      </div>
      <p className="text-xs font-medium text-foreground">{caption}</p>
      {/* Paths are code — always LTR regardless of UI direction. */}
      <p dir="ltr" className="break-all font-mono text-[11px] text-muted-foreground">
        {url}
      </p>
      <p
        className={`text-[11px] font-medium ${status === 'ok' ? 'text-accent' : status === 'error' ? 'text-error' : 'text-muted-foreground'}`}
      >
        {status === 'ok'
          ? t('tasks.coverImage.loaded')
          : status === 'error'
            ? t('tasks.coverImage.notFound')
            : '…'}
      </p>
    </div>
  )
}

export const CoverImagePreview: React.FC<CoverImagePreviewProps> = ({ base, focusY }) => {
  const { t } = useTar()

  const trimmed = base?.trim()
  if (trimmed === undefined || trimmed === '') return null

  const variants: { size: CoverImageSize; caption: string; boxClassName: string }[] = [
    {
      size: 'lg',
      // Match the live challenge card's cover box (22rem × h-28) so the preview
      // crop is exactly what the card shows; the drawer shares the same focal
      // center via the aspect-aware object-position.
      caption: t('tasks.coverImage.lgCaption', { px: COVER_IMAGE_SIZES.lg }),
      boxClassName: 'h-28 w-[22rem] max-w-full',
    },
    {
      size: 'sm',
      caption: t('tasks.coverImage.smCaption', { px: COVER_IMAGE_SIZES.sm }),
      boxClassName: 'h-16 w-16',
    },
  ]

  return (
    <div className="mt-3 space-y-3 rounded-md border border-dashed border-border bg-muted/20 p-3">
      <p className="text-xs text-muted-foreground">{t('tasks.coverImage.howItWorks')}</p>
      <div className="flex flex-wrap items-start gap-4">
        {variants.map(({ size, caption, boxClassName }) => (
          <VariantPreview
            key={`${size}-${trimmed}`}
            url={toCoverVariantUrl(trimmed, COVER_IMAGE_SIZES[size])}
            caption={caption}
            boxClassName={boxClassName}
            focusY={focusY}
          />
        ))}
      </div>
    </div>
  )
}
